<template>
    <div class="code-block" :class="{'contracted': !isExpanded}">
        <div class="code-block-head">
            <div class="code-block-title">{{ name || "" }}</div>
            <div class="code-block-buttons">
                <button v-if="!isExpanded" type="button" class="code-block-btn" @click="expand" :title="$t('Expand code fragment')">
                    <i class="fas fa-chevron-down"></i>
                </button>
                <button v-if="isExpanded" type="button" class="code-block-btn" @click="contract" :title="$t('Contract code fragment')">
                    <i class="fas fa-chevron-up"></i>
                </button>
                <button type="button" class="code-block-btn" @click="copyCode" :title="$t('Copy')">
                    <i class="fas fa-clone"></i>
                </button>
            </div>
        </div>
        <pre v-if="language !== 'raw'"><code tabindex="0" v-html="highlightedCode"></code></pre>
        <code v-else class="raw-code-block" v-html="highlightedCode"></code>
        <button v-if="!isExpanded" type="button" class="code-block-expand-button" @click="expand">
            {{ $t("Expand") }}
        </button>
    </div>
</template>

<script lang="ts">
import { PropType, defineComponent } from "vue";
import hljs from "highlight.js/lib/core";
import "highlight.js/styles/agate.min.css";
import hljsJson from 'highlight.js/lib/languages/json';
import { escapeHTML } from "@/utils/text";

hljs.registerLanguage('json', hljsJson);

export type CodeBlockLanguage = "json" | "raw";

export default defineComponent({
    name: "CodeBlock",
    props: {
        name: String,
        expandable: Boolean,
        code: String,
        language: String as PropType<CodeBlockLanguage>,
    },
    data: function () {
        return {
            isExpanded: !this.expandable,
        };
    },
    computed: {
        highlightedCode: function (): string {
            if (!this.code) {
                return "";
            }

            if (!this.language || this.language === "raw") {
                return escapeHTML(this.code).replace(/\n/g, "<br>");
            }

            return hljs.highlight(this.code, {
                language: this.language,
            }).value;
        },
    },
    methods: {
        copyCode: function () {
            navigator.clipboard.writeText(this.code);
            this.$showSnackBar(this.$t("Code fragment successfully copied to clipboard!"));
        },

        expand: function () {
            this.isExpanded = true;
        },

        contract: function () {
            this.isExpanded = false;
        },
    },
});
</script>

<style>
.code-block {
    display: block;
    background: black;
    color: white;
    padding: 0.5rem;
    border-radius: 0.25rem;
    position: relative;
}

.code-block pre {
    overflow: auto;
}

.code-block.contracted {
    overflow: hidden;
    max-height: 300px;
}

.code-block-head {
    display: flex;
    flex-direction: row;
    padding-bottom: 0.25rem;
    border-bottom: solid 1px rgba(255, 255, 255, 0.1);
    align-items: center;
}

.code-block-title {
    flex: 1;
    min-width: 1px;
    overflow: hidden;
    text-overflow: ellipsis;
    color: lightgray;
    font-family: monospace;
}

.code-block-btn {
    color: white;
    background: transparent;
    font-size: large;
    outline: none;
    border: none;
    cursor: pointer;
    opacity: 0.8;
}

.code-block-btn:hover, .code-block-btn:focus {
    opacity: 1;
}

.code-block-expand-button {
    display: flex;
    position: absolute;
    justify-content: center;
    align-items: center;
    bottom: 0;
    left: 0;
    right: 0;
    outline: none;
    border: none;
    opacity: 0.8;
    padding: 0.5rem;
    font-size: large;
    background: black;
    color: white;
    cursor: pointer;
}

.code-block-expand-button:hover, .code-block-expand-button:focus {
    opacity: 0.9;
}

.raw-code-block {
    line-break: anywhere;
    word-wrap: break-word;
    display: block;
    color: limegreen;
}
</style>
